import { useState } from 'react';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { trackFormComplete } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const AGE_OPTIONS = [
  { value: 'yes', label: 'Oui, j\u2019ai entre 18 et 30 ans' },
  { value: 'no', label: 'Non' },
];

const SITUATION_OPTIONS = [
  { value: 'zero_ready', label: 'Je pars de zéro et je suis prêt(e) à m\u2019investir financièrement et temporellement' },
  { value: 'under_1k', label: 'J\u2019ai un business qui me génère moins de 1\u00A0000€/mois' },
  { value: '1k_5k', label: 'J\u2019ai un business qui me génère entre 1\u00A0000 et 5\u00A0000€/mois' },
];

const stepTitles = [
  'Tes informations',
  'Ton âge',
  'Où tu en es aujourd\u2019hui',
];

const StepDots = ({ step }) => (
  <div className="flex justify-center gap-2" data-testid="step-dots">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: i === step ? 24 : 6,
          height: 6,
          borderRadius: 3,
          background:
            i <= step ? '#F5EFE3' : 'rgba(245, 239, 227, 0.2)',
          transition: 'all 0.3s ease',
        }}
      />
    ))}
  </div>
);

const SelectableTile = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="w-full text-left transition-all"
    style={{
      background: selected ? '#2A5F44' : '#1F4A34',
      border: selected
        ? '2px solid #F5EFE3'
        : '1px solid rgba(245, 239, 227, 0.15)',
      borderRadius: 12,
      padding: selected ? '13px 17px' : '14px 18px',
      minHeight: 56,
      color: '#F5EFE3',
      fontSize: 16,
      fontWeight: selected ? 600 : 500,
      lineHeight: 1.35,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      cursor: 'pointer',
    }}
  >
    <span style={{ flex: 1 }}>{children}</span>
    {selected && <Check size={18} strokeWidth={2.5} style={{ flexShrink: 0 }} />}
  </button>
);

export const QualificationForm = ({ onQualified, onClose }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    instagram: '',
    age_range: '',
    situation: '',
  });

  const update = (field, value) => setData((d) => ({ ...d, [field]: value }));

  const updateInstagram = (value) => {
    // Strip any leading @ so the visible prefix stays clean.
    const clean = value.replace(/^@+/, '').trim();
    setData((d) => ({ ...d, instagram: clean }));
  };

  const isStepValid = () => {
    if (step === 0) {
      return (
        data.first_name.trim().length > 0 &&
        data.last_name.trim().length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()) &&
        data.phone.trim().length >= 6 &&
        data.instagram.trim().length > 0
      );
    }
    if (step === 1) return !!data.age_range;
    if (step === 2) return !!data.situation;
    return false;
  };

  // Hard-block screen when user is NOT 18-30
  const isBlocked = step === 1 && data.age_range === 'no';

  const next = async () => {
    setError(null);
    if (!isStepValid()) return;
    if (isBlocked) return;
    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        instagram: data.instagram.trim(),
        // Map Yes/No to the backend age_range codes so the eligibility rule keeps working
        age_range: data.age_range === 'yes' ? '18_30' : 'less_than_18',
        situation: data.situation,
      };
      const res = await fetch(`${API}/api/validate-qualification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok || result.eligible === false) {
        setError(result.message || "Une erreur est survenue. Réessaie dans un instant.");
        return;
      }
      trackFormComplete();
      onQualified(payload);
    } catch (e) {
      setError("Une erreur est survenue. Réessaie dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  const prev = () => {
    setError(null);
    if (step > 0) setStep((s) => s - 1);
  };

  return (
    <ModalShell onClose={onClose}>
      <div
        data-testid="qualification-form"
        className="flex flex-col"
        style={{ padding: '20px 20px 24px 20px', gap: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={step === 0 ? onClose : prev}
            aria-label="Retour"
            data-testid="back-btn"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'rgba(245, 239, 227, 0.06)',
              border: '1px solid rgba(245, 239, 227, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#F5EFE3',
              cursor: 'pointer',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <p
            style={{
              color: '#F5EFE3',
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            Réserve ton appel
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="close-btn"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'rgba(245, 239, 227, 0.06)',
              border: '1px solid rgba(245, 239, 227, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#F5EFE3',
              cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Dots */}
        <StepDots step={step} />

        {/* Step title */}
        <div className="flex flex-col gap-2">
          <p className="microlabel">Étape {step + 1} / 3</p>
          <h2
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 800,
              fontSize: 24,
              lineHeight: 1.15,
              color: '#F5EFE3',
              letterSpacing: '-0.02em',
            }}
          >
            {stepTitles[step]}
          </h2>
        </div>

        {/* Step body */}
        {step === 0 && (
          <div className="flex flex-col gap-3">
            <Input
              value={data.first_name}
              onChange={(v) => update('first_name', v)}
              placeholder="Prénom"
              testId="input-first-name"
            />
            <Input
              value={data.last_name}
              onChange={(v) => update('last_name', v)}
              placeholder="Nom"
              testId="input-last-name"
            />
            <Input
              value={data.email}
              onChange={(v) => update('email', v)}
              placeholder="Adresse e-mail"
              type="email"
              testId="input-email"
            />
            <Input
              value={data.phone}
              onChange={(v) => update('phone', v)}
              placeholder="Numéro de téléphone (avec indicatif)"
              type="tel"
              testId="input-phone"
            />
            <InstagramInput
              value={data.instagram}
              onChange={updateInstagram}
              testId="input-instagram"
            />
          </div>
        )}

        {step === 1 && (
          <div className="flex flex-col gap-2.5">
            <p
              style={{
                color: 'rgba(245, 239, 227, 0.6)',
                fontSize: 14,
                lineHeight: 1.5,
                marginBottom: 4,
              }}
            >
              L&rsquo;accompagnement HDA s&rsquo;adresse aux 18-30 ans.
            </p>
            {AGE_OPTIONS.map((opt) => (
              <SelectableTile
                key={opt.value}
                selected={data.age_range === opt.value}
                onClick={() => update('age_range', opt.value)}
                testId={`age-${opt.value}`}
              >
                {opt.label}
              </SelectableTile>
            ))}
            {isBlocked && (
              <div
                data-testid="age-block-message"
                style={{
                  marginTop: 8,
                  padding: '14px 16px',
                  background: 'rgba(245, 239, 227, 0.06)',
                  border: '1px solid rgba(245, 239, 227, 0.15)',
                  borderRadius: 12,
                  color: '#F5EFE3',
                  fontSize: 14,
                  lineHeight: 1.5,
                }}
              >
                Désolé, l&rsquo;accompagnement HDA est actuellement réservé aux 18-30 ans. Nous
                développons d&rsquo;autres offres — reviens nous voir bientôt.
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-2.5">
            {SITUATION_OPTIONS.map((opt) => (
              <SelectableTile
                key={opt.value}
                selected={data.situation === opt.value}
                onClick={() => update('situation', opt.value)}
                testId={`situation-${opt.value}`}
              >
                {opt.label}
              </SelectableTile>
            ))}
          </div>
        )}

        {error && (
          <p style={{ color: '#F5EFE3', fontSize: 14 }} data-testid="form-error">
            {error}
          </p>
        )}

        {/* CTA */}
        <button
          onClick={next}
          disabled={!isStepValid() || loading || isBlocked}
          data-testid="next-btn"
          className="btn-primary w-full"
        >
          {loading ? 'Envoi…' : step === 2 ? 'Voir mon créneau' : 'Continuer'}
          {!loading && <ArrowRight size={18} className="ml-2" strokeWidth={2.5} />}
        </button>

        {/* RGPD */}
        <p
          className="text-center"
          style={{ color: 'rgba(245, 239, 227, 0.45)', fontSize: 12, marginTop: -4 }}
        >
          Tes informations sont protégées conformément au RGPD. Aucun spam.
        </p>
      </div>
    </ModalShell>
  );
};

const Input = ({ value, onChange, placeholder, type = 'text', testId }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    style={{
      width: '100%',
      minHeight: 56,
      padding: '16px 18px',
      background: '#1F4A34',
      border: '1px solid rgba(245, 239, 227, 0.15)',
      borderRadius: 12,
      color: '#F5EFE3',
      fontSize: 16,
      outline: 'none',
      fontFamily: 'Inter, sans-serif',
    }}
    onFocus={(e) => {
      e.target.style.borderColor = '#F5EFE3';
    }}
    onBlur={(e) => {
      e.target.style.borderColor = 'rgba(245, 239, 227, 0.15)';
    }}
  />
);

const InstagramInput = ({ value, onChange, testId }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'stretch',
      background: '#1F4A34',
      border: '1px solid rgba(245, 239, 227, 0.15)',
      borderRadius: 12,
      overflow: 'hidden',
      minHeight: 56,
    }}
  >
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 4px 0 18px',
        color: 'rgba(245, 239, 227, 0.55)',
        fontSize: 16,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      @
    </span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="pseudo Instagram"
      data-testid={testId}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck="false"
      style={{
        flex: 1,
        padding: '16px 18px 16px 0',
        background: 'transparent',
        border: 'none',
        color: '#F5EFE3',
        fontSize: 16,
        outline: 'none',
        fontFamily: 'Inter, sans-serif',
      }}
    />
  </div>
);

const ModalShell = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
    style={{ background: 'rgba(0, 0, 0, 0.75)' }}
    onClick={onClose}
    data-testid="modal-backdrop"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full sm:max-w-md sm:rounded-2xl overflow-hidden"
      style={{
        background: '#0D1F17',
        border: '1px solid rgba(245, 239, 227, 0.12)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        maxHeight: '92vh',
        overflowY: 'auto',
      }}
    >
      {children}
    </div>
  </div>
);

export default QualificationForm;
